import { useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import type { NotificationDelivery } from '@bekebi/contracts/api';
import { ApiError } from '../../api/client';
import { Button } from '../../components/button';
import { AlertIcon, CheckCircleIcon, MailIcon } from '../../components/icons';
import { ErrorState, LoadingState } from '../../components/states';
import { useHasPermission } from '../../session/current-user';
import { formatDateTime } from '../services/format';
import { useNotifications, useSendTestEmail } from './api';
import {
  DELIVERY_KIND_TEXT,
  DeliveryStateBadge,
  deliveryDetail,
  recipientLabel,
} from './notification-present';

/**
 * Alert emails on Monitoring → Alerts (Phase 07 Stage 05-C, Admin, `monitoring.view`): whether
 * alert emails are set up, whether they are failing, and the recent alert and recovery emails,
 * all from GET /api/monitoring/notifications. With `monitoring.manage`, the Admin can send a test
 * email to themselves. There are no recipient, preference or setting controls: who receives alert
 * emails is fixed (every active Admin and ICT user), and the mail settings live on the server.
 */
export function NotificationsSection() {
  const notifications = useNotifications();
  const canManage = useHasPermission('monitoring.manage');
  const first = notifications.data?.pages[0];
  const items = notifications.data?.pages.flatMap((p) => p.items) ?? [];
  const { hash } = useLocation();
  const loaded = notifications.isSuccess;

  // Arriving from the Dashboard's "Alert emails are failing" link: bring this section into view.
  useEffect(() => {
    if (hash === '#alert-emails' && loaded) {
      document.getElementById('alert-emails')?.scrollIntoView?.({ block: 'start' });
    }
  }, [hash, loaded]);

  return (
    <section
      id="alert-emails"
      aria-labelledby="alert-emails-title"
      className="scroll-mt-6 rounded-[var(--radius-card)] border border-line bg-surface p-5 sm:p-6"
    >
      <h2 id="alert-emails-title">Alert emails</h2>
      <p className="mt-1 text-sm text-muted">
        Every active Admin and ICT user is emailed when an alert has been open for 10 minutes, and
        again when the service recovers. &ldquo;Accepted by the mail server&rdquo; means the mail
        server took the email; it does not confirm that it reached the inbox.
      </p>
      <div className="mt-4 space-y-4">
        {notifications.isPending ? (
          <LoadingState label="Loading alert emails…" />
        ) : notifications.isError ? (
          <ErrorState
            title="Alert emails could not be loaded"
            message={notifications.error.message}
            onRetry={() => void notifications.refetch()}
          />
        ) : (
          <>
            <EmailState emailSetUp={first!.emailSetUp} failing={first!.failing} />
            {canManage && first!.emailSetUp ? <TestEmail /> : null}
            {items.length === 0 ? (
              <p className="rounded-xl border border-dashed border-line px-4 py-3 text-sm text-muted">
                No alert emails yet. They are planned when an alert has been open for 10 minutes.
              </p>
            ) : (
              <>
                <ul aria-label="Recent alert emails" className="divide-y divide-line">
                  {items.map((delivery) => (
                    <li key={delivery.id} className="py-3 first:pt-0 last:pb-0">
                      <DeliveryRow delivery={delivery} showAlert />
                    </li>
                  ))}
                </ul>
                {notifications.hasNextPage ? (
                  <Button
                    variant="secondary"
                    onClick={() => void notifications.fetchNextPage()}
                    disabled={notifications.isFetchingNextPage}
                  >
                    {notifications.isFetchingNextPage ? 'Loading…' : 'Show older alert emails'}
                  </Button>
                ) : null}
                {notifications.isFetchNextPageError ? (
                  <p role="alert" className="text-sm text-danger">
                    Older alert emails could not be loaded. Try again.
                  </p>
                ) : null}
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}

/** Set up or not (from the server's settings), and failing (from the deliveries' own outcomes). */
function EmailState({ emailSetUp, failing }: { emailSetUp: boolean; failing: boolean }) {
  if (failing) {
    return (
      <div
        role="status"
        className="flex items-start gap-3 rounded-xl bg-danger-soft/70 px-4 py-3 text-sm"
      >
        <AlertIcon className="mt-0.5 size-5 shrink-0 text-danger" />
        <p className="text-fg-2">
          <span className="font-semibold text-fg">Alert emails are failing.</span> The most recent
          alert email was not accepted by the mail server. See the reason below.
          {emailSetUp ? null : ' Alert emails are also not set up on the server any more.'}
        </p>
      </div>
    );
  }
  if (!emailSetUp) {
    return (
      <div
        role="status"
        className="flex items-start gap-3 rounded-xl border border-line bg-canvas/40 px-4 py-3 text-sm"
      >
        <MailIcon className="mt-0.5 size-5 shrink-0 text-muted" />
        <p className="text-fg-2">
          <span className="font-semibold text-fg">Alert emails are not set up.</span> Alerts still
          open and resolve, and appear here and on the Dashboard, but no email is sent until the
          mail settings are added on the server.
        </p>
      </div>
    );
  }
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-xl bg-success-soft/60 px-4 py-3 text-sm"
    >
      <CheckCircleIcon className="mt-0.5 size-5 shrink-0 text-success" />
      <p className="text-fg-2">
        <span className="font-semibold text-fg">Alert emails are set up.</span> No recent alert
        email has failed.
      </p>
    </div>
  );
}

/** One alert or recovery email: what, to whom (no address), its state, and when. */
export function DeliveryRow({
  delivery,
  showAlert = false,
}: {
  delivery: NotificationDelivery;
  showAlert?: boolean;
}) {
  return (
    <article className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 text-sm">
      <div className="min-w-0">
        <p className="font-semibold text-fg">
          {DELIVERY_KIND_TEXT[delivery.kind]}
          {showAlert ? (
            <>
              {' · '}
              <Link
                to={`/app/monitoring/alerts/${delivery.alert.id}`}
                className="text-accent-link underline decoration-accent-link/40 underline-offset-4 hover:decoration-accent-link"
              >
                {delivery.alert.service.name} alert
              </Link>
            </>
          ) : null}
        </p>
        <p className="mt-0.5 text-fg-2">To {recipientLabel(delivery)}</p>
        <p className="mt-0.5 text-xs text-muted">{deliveryDetail(delivery)}</p>
      </div>
      <DeliveryStateBadge delivery={delivery} />
    </article>
  );
}

/** The Admin's test email — to their own address only; the request names no recipient. */
function TestEmail() {
  const send = useSendTestEmail();
  return (
    <div className="rounded-xl border border-line bg-canvas/40 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-fg-2">
          <span className="font-semibold text-fg">Test email.</span> Sends a test message to your
          own email address to check that alert emails can be sent. It is not an alert.
        </p>
        <Button variant="secondary" onClick={() => send.mutate()} disabled={send.isPending}>
          {send.isPending ? 'Sending test email…' : 'Send test email to me'}
        </Button>
      </div>
      {send.isSuccess ? (
        <p role="status" className="mt-2 text-success">
          Test email accepted by the mail server at {formatDateTime(send.data.acceptedAt)}. Check
          your inbox; acceptance does not confirm that it arrived.
        </p>
      ) : send.isError ? (
        <p role="alert" className="mt-2 text-danger">
          The test email was not sent:{' '}
          {send.error instanceof ApiError ? send.error.message : 'something went wrong.'}
        </p>
      ) : null}
    </div>
  );
}
